import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@ui/Button"
import { Dialog } from "@ui/Dialog"
import { Input } from "@ui/Input"
import { Spinner } from "@ui/Spinner"
import { describeRecurrenceText, recurrenceEndDate } from "./formatters"
import MeetingsIcon from "../../icons/cisco-momentum/core/meetings-regular.svg?react"
import SearchIcon from "../../icons/cisco-momentum/core/search-regular.svg?react"
import RecurringIcon from "../../icons/cisco-momentum/core/recurring-regular.svg?react"

function formatMeetingDate(dateStr) {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function formatMeetingTime(dateStr) {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
}

export default function MeetingLinkModal({ isOpen, onClose, courseId, courseSpaces = [] }) {
  const [ searchTerm, setSearchTerm ] = useState("")
  const [ results, setResults ] = useState([])
  const [ searching, setSearching ] = useState(false)
  const [ selectedId, setSelectedId ] = useState(null)
  const [ selectedSpaceId, setSelectedSpaceId ] = useState(null)
  const [ step, setStep ] = useState("meeting")
  const [ linking, setLinking ] = useState(false)
  const [ error, setError ] = useState(null)
  const searchTimeout = useRef(null)

  useEffect(() => {
    if (isOpen) {
      fetchMeetings("")
      setStep("meeting")
    } else {
      setSearchTerm("")
      setResults([])
      setSelectedId(null)
      setSelectedSpaceId(null)
      setStep("meeting")
      setError(null)
      setLinking(false)
    }
  }, [ isOpen ])

  async function fetchMeetings(q) {
    setSearching(true)
    setError(null)
    try {
      const url = q
        ? `/lti/courses/${courseId}/course_meetings/available?q=${encodeURIComponent(q)}`
        : `/lti/courses/${courseId}/course_meetings/available`
      const res = await fetch(url)
      const data = await res.json()
      setResults(data.meetings || [])
      if (data.error) setError(data.error)
    } catch {
      setError("Failed to load meetings")
    } finally {
      setSearching(false)
    }
  }

  function handleSearch(q) {
    setSearchTerm(q)
    setSelectedId(null)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      fetchMeetings(q)
    }, 300)
  }

  function handleNext() {
    if (!selectedId) return
    if (courseSpaces.length > 1) {
      setStep("space")
      setSelectedSpaceId(courseSpaces[0]?.scheduled_space_id || null)
    } else {
      doLink(courseSpaces[0]?.scheduled_space_id || null)
    }
  }

  async function doLink(spaceId) {
    setLinking(true)
    setError(null)
    const body = { webex_meeting_id: selectedId }
    if (spaceId) body.scheduled_space_id = spaceId
    try {
      const res = await fetch(`/lti/courses/${courseId}/course_meetings/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
      if (res.ok) {
        onClose()
        router.reload()
      } else {
        const data = await res.json()
        setError(data.error || "Failed to link meeting")
      }
    } catch {
      setError("Failed to link meeting")
    } finally {
      setLinking(false)
    }
  }

  const selectedMeeting = results.find(m => m.id === selectedId)

  return (
    <Dialog.Root open={isOpen} onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Content size="md">
        <Dialog.Header>
          <Dialog.Title>{step === "space" ? "Select Space" : "Link Existing Meeting"}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          {step === "meeting" && (
            <div className="space-y-4">
              <Input
                type="text"
                value={searchTerm}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search meetings..."
                leadingIcon={<SearchIcon aria-hidden="true" />}
              />

              <div className="min-h-[256px] max-h-64 overflow-y-auto border border-webex-gray-300 dark:border-webex-gray-700 rounded-md relative">
                {searching && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/60 dark:bg-webex-gray-950/60">
                    <Spinner size="md" />
                  </div>
                )}
                {results.length === 0 && !searching ? (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-webex-gray-600 dark:text-webex-gray-400">
                    {searchTerm ? "No matching meetings found" : "No available meetings"}
                  </div>
                ) : (
                  <ul className="divide-y divide-webex-gray-200 dark:divide-webex-gray-800">
                    {results.map(meeting => (
                      <li key={meeting.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(meeting.id)}
                          className={`w-full px-4 py-3 text-left transition-colors motion-reduce:transition-none ${
                            selectedId === meeting.id
                              ? "bg-webex-blue-100 dark:bg-webex-blue-900"
                              : "hover:bg-webex-gray-50 dark:hover:bg-webex-gray-900"
                          }`}
                        >
                          <div className={`text-sm flex items-center gap-2 ${
                            selectedId === meeting.id ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-1000 dark:text-white"
                          }`}>
                            <MeetingsIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
                            <span className="truncate">{meeting.title}</span>
                          </div>
                          <div className={`text-xs mt-0.5 ml-6 flex items-center gap-1.5 ${
                            selectedId === meeting.id ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-600 dark:text-webex-gray-400"
                          }`}>
                            {meeting.recurring
                              ? `${formatMeetingDate(meeting.start)} - ${recurrenceEndDate(meeting.recurrence) ? formatMeetingDate(recurrenceEndDate(meeting.recurrence)) : "Ongoing"}`
                              : formatMeetingDate(meeting.start)
                            }
                            {meeting.start && ` at ${formatMeetingTime(meeting.start)}`}
                            {meeting.recurring && (
                              <span className="inline-flex items-center gap-1">
                                <RecurringIcon className="w-3 h-3 shrink-0" aria-hidden="true" />
                                {describeRecurrenceText(meeting.recurrence)}
                              </span>
                            )}
                          </div>
                          {meeting.host_display_name && (
                            <div className={`text-xs mt-0.5 ml-6 ${
                              selectedId === meeting.id ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-600 dark:text-webex-gray-400"
                            }`}>
                              Host: {meeting.host_display_name}
                            </div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {error && (
                <p className="text-xs text-webex-red-700 dark:text-webex-red-400">{error}</p>
              )}

              {results.length > 0 && (
                <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400">
                  {results.length} meeting{results.length === 1 ? "" : "s"} available
                </p>
              )}
            </div>
          )}

          {step === "space" && (
            <div className="space-y-4">
              {selectedMeeting && (
                <p className="text-sm text-webex-gray-700 dark:text-webex-gray-300">
                  Linking <span className="font-medium text-webex-gray-1000 dark:text-white">{selectedMeeting.title}</span>
                </p>
              )}
              <p className="text-sm text-webex-gray-600 dark:text-webex-gray-400">Select a space to assign this meeting to:</p>
              <div className="border border-webex-gray-300 dark:border-webex-gray-700 rounded-md divide-y divide-webex-gray-200 dark:divide-webex-gray-800">
                {courseSpaces.map(cs => (
                  <button
                    key={cs.scheduled_space_id}
                    type="button"
                    onClick={() => setSelectedSpaceId(cs.scheduled_space_id)}
                    className={`w-full px-4 py-3 text-left transition-colors motion-reduce:transition-none ${
                      selectedSpaceId === cs.scheduled_space_id
                        ? "bg-webex-blue-100 dark:bg-webex-blue-900"
                        : "hover:bg-webex-gray-50 dark:hover:bg-webex-gray-900"
                    }`}
                  >
                    <div className={`text-sm font-medium ${
                      selectedSpaceId === cs.scheduled_space_id ? "text-webex-blue-700 dark:text-webex-blue-400" : "text-webex-gray-1000 dark:text-white"
                    }`}>
                      {cs.external_name || cs.name}
                    </div>
                    {cs.name && cs.external_name && (
                      <p className="text-xs text-webex-gray-500 dark:text-webex-gray-400 mt-0.5">{cs.name}</p>
                    )}
                  </button>
                ))}
              </div>

              {error && (
                <p className="text-xs text-webex-red-700 dark:text-webex-red-400">{error}</p>
              )}
            </div>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          {step === "space" && (
            <Button variant="secondary" onClick={() => setStep("meeting")}>Back</Button>
          )}
          {step === "meeting" && (
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          )}
          <Button
            variant="primary"
            onClick={step === "space" ? () => doLink(selectedSpaceId) : handleNext}
            disabled={step === "meeting" ? !selectedId : !selectedSpaceId || linking}
          >
            {linking ? "Linking..." : step === "space" ? "Link" : courseSpaces.length > 1 ? "Next" : "Link"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
